import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, imageUrl, categoryLabel } from "../lib/api";
import { Sparkles, Star } from "lucide-react";

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";

  useEffect(() => {
    const q = category && category !== "all" ? `?category=${category}` : "";
    api.get(`/products${q}`).then((r) => setProducts(r.data)).catch(() => {});
  }, [category]);

  const setCat = (c) => {
    if (c === "all") setParams({});
    else setParams({ category: c });
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="catalog-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <div className="font-mono-ui text-[11px] tracking-[0.3em] text-[var(--crimson)]">{category === "handmade" ? "MAWMAW'S HANDMADE" : "THE SHOP"}</div>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[var(--navy)] mt-3 tracking-tight">{category === "handmade" ? "Handmade with love." : "Pick your blank."}</h1>
          <p className="mt-4 text-[var(--ink-soft)] max-w-xl">{category === "handmade" ? "One-of-a-kind pieces MawMaw makes by hand. When they're gone, they're gone." : "Pick a base, slap a design on it (ours or yours), and Papa will press it within the week."}</p>
        </div>
        <div className={`flex flex-wrap gap-2 font-mono-ui text-[11px] tracking-[0.2em] uppercase ${category === "handmade" ? "hidden" : ""}`}>
          {[
            { key: "all", label: "All" },
            { key: "tshirt", label: "T-Shirts" },
            { key: "tumbler", label: "Tumblers" },
          ].map((c) => (
            <button
              key={c.key}
              onClick={() => setCat(c.key)}
              data-testid={`filter-${c.key}`}
              className={`px-4 py-2.5 rounded-sm border transition ${
                category === c.key
                  ? "bg-[var(--navy)] text-[var(--bone)] border-[var(--navy)]"
                  : "bg-transparent text-[var(--navy)] border-[rgba(27,42,65,0.2)] hover:border-[var(--navy)]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {products.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
      {products.length === 0 && (
        <div className="text-center py-20 text-[var(--ink-soft)]" data-testid="catalog-empty">No goods in this category yet.</div>
      )}

      <div className="mt-14 card-rustic p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4 border-l-4 border-[var(--gold)]" data-testid="catalog-bulk-banner">
        <div>
          <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--crimson)]">Bulk &amp; Wholesale</div>
          <h3 className="font-serif-display text-2xl md:text-3xl text-[var(--navy)] mt-1">Ordering for a team, church, or business?</h3>
          <p className="text-[var(--ink-soft)] mt-1">Contact us for a custom quote — special pricing on bulk runs.</p>
        </div>
        <Link to="/contact" className="btn-primary btn-crimson whitespace-nowrap justify-center" data-testid="catalog-bulk-cta">Contact for a Quote</Link>
      </div>
    </div>
  );
}

function ProductCard({ product }) {
  return (
    <div className="group" data-testid={`product-card-${product.id}`}>
      <Link to={`/product/${product.id}`} className="block" data-testid={`product-link-${product.id}`}>
        <div className="aspect-[4/5] overflow-hidden rounded-sm bg-[var(--bone-2)] border border-[rgba(27,42,65,0.08)] relative">
          <img src={imageUrl(product.mockup_image)} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>
          <div className="absolute top-3 left-3 stamp-badge">
            {categoryLabel(product.category)}
          </div>
          {product.featured && (
            <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[var(--gold)] text-[var(--navy)] px-2.5 py-1 rounded-sm font-mono-ui text-[9px] tracking-[0.2em] uppercase shadow" data-testid={`product-featured-badge-${product.id}`}>
              <Star className="w-3 h-3 fill-[var(--navy)]"/> Bestseller
            </div>
          )}
        </div>
        <div className="mt-4 flex items-start justify-between gap-3">
          <div>
            <div className="font-serif-display text-2xl text-[var(--navy)] leading-tight">{product.name}</div>
            <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)] mt-1">{product.colors?.length || 0} COLORS · {product.preset_designs?.length || 0} PRESETS</div>
            {(product.exclude_from_discounts || product.exclude_from_free_shipping) && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {product.exclude_from_discounts && (
                  <span className="inline-block bg-[var(--crimson)]/10 text-[var(--crimson)] px-2 py-0.5 rounded-sm font-mono-ui text-[8px] tracking-[0.15em] uppercase" data-testid={`product-no-discount-badge-${product.id}`}>No Discounts</span>
                )}
                {product.exclude_from_free_shipping && (
                  <span className="inline-block bg-[var(--navy)]/10 text-[var(--navy)] px-2 py-0.5 rounded-sm font-mono-ui text-[8px] tracking-[0.15em] uppercase" data-testid={`product-no-freeship-badge-${product.id}`}>Shipping Applies</span>
                )}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="font-mono-ui text-[10px] tracking-[0.2em] text-[var(--ink-soft)]">FROM</div>
            <div className="font-serif-display text-2xl text-[var(--crimson)]">${product.base_price.toFixed(0)}</div>
          </div>
        </div>
      </Link>

      <Link
        to={`/product/${product.id}`}
        data-testid={`product-customize-${product.id}`}
        className="mt-3 w-full flex items-center justify-center gap-2 bg-[var(--navy)] hover:bg-[var(--navy-deep)] text-[var(--bone)] py-3 rounded-sm font-mono-ui text-[11px] tracking-[0.22em] uppercase transition"
      >
        <Sparkles className="w-3.5 h-3.5"/> Customize
      </Link>
    </div>
  );
}
